import { pdf } from '@react-pdf/renderer';
import type { ReactElement } from 'react';

// Marker id for the Project Overview card so it can be located inside react-pdf's
// laid-out node tree (ids survive layout; react-pdf also uses them as link anchors).
export const OVERVIEW_CARD_ID = 'project-overview-card';

// Below this the 11pt body copy drops under ~8pt and stops being comfortably legible;
// past the floor we give up on fitting and let the card flow to page 2 as before.
const MIN_OVERVIEW_SCALE = 0.75;
const MAX_FIT_PASSES = 6;

// A page carrying less than this much non-fixed content is visually empty — roughly two
// lines of 11pt body copy. Real pages clear it by hundreds of points; the failure mode
// this catches is a hairline or a stray heading orphaned onto a page of its own.
const BLANK_PAGE_CONTENT_FLOOR = 24;

// A *non-final* page filling less than this share of its content area isn't blank but is
// still a layout bug: the flow had more to give and something pushed it to the next page.
// The Sept 2026 ATI one-pager hit this — one criteria row (68pt) alone on a 690pt page,
// far above the blank floor, so the check below said nothing.
const UNDERFULL_PAGE_FILL_RATIO = 0.35;

// Shape of react-pdf's internal layout tree (surfaced via Document onRender as
// _INTERNAL__LAYOUT__DATA_). Only the fields the fit check reads are typed here.
type LayoutNode = {
  type?: string;
  box?: { top?: number; height?: number; marginBottom?: number };
  style?: { height?: number; paddingTop?: number; paddingBottom?: number };
  props?: { id?: string; fixed?: boolean };
  children?: LayoutNode[];
};

const collectById = (node: LayoutNode, id: string, out: LayoutNode[]): void => {
  if (node.props?.id === id) out.push(node);
  (node.children ?? []).forEach((child) => collectById(child, id, out));
};

// Bottom edge (incl. margin) of the lowest non-fixed element on a page — where the
// next flowed element would start. Used when the card was pushed wholly off page 1.
const furthestContentBottom = (page: LayoutNode): number => {
  let bottom = 0;
  (page.children ?? []).forEach((child) => {
    if (child.props?.fixed) return;
    const top = child.box?.top ?? 0;
    const height = child.box?.height ?? 0;
    const marginBottom = child.box?.marginBottom ?? 0;
    bottom = Math.max(bottom, top + height + marginBottom);
  });
  return bottom;
};

// Total height of a page's own flowed (non-fixed) children. Fixed nodes — the footer —
// are on every page and say nothing about whether the page carries content.
const flowedContentHeight = (page: LayoutNode): number =>
  (page.children ?? [])
    .filter((child) => !child.props?.fixed)
    .reduce((sum, child) => sum + (child.box?.height ?? 0), 0);

/**
 * 1-based numbers of pages that rendered visually empty. A page break can strand a
 * near-zero-height node (a rule, a lone heading) on a page by itself, which then also
 * defeats react-pdf's "already at the top of the page" guard for the next section's
 * minPresenceAhead — so one orphan can cost a whole blank page.
 */
const findBlankPages = (layout: LayoutNode | undefined): number[] =>
  (layout?.children ?? [])
    .map((page, i) => (flowedContentHeight(page) < BLANK_PAGE_CONTENT_FLOOR ? i + 1 : 0))
    .filter((n) => n > 0);

const contentArea = (page: LayoutNode): number =>
  (page.style?.height ?? 792) - (page.style?.paddingTop ?? 0) - (page.style?.paddingBottom ?? 0);

/**
 * 1-based numbers of non-final pages that came out mostly empty. Distinct from a blank
 * page: the page carries real content, just far too little for the flow to have stopped
 * there. Usually a section that fits the page but was moved off it wholesale — react-pdf
 * does that when the node's own bottom margin plus its `minPresenceAhead` reaches past the
 * page's wrap boundary, which is why section containers here don't carry minPresenceAhead.
 */
const findUnderfullPages = (layout: LayoutNode | undefined): number[] => {
  const pages = layout?.children ?? [];
  return pages
    .slice(0, -1)
    .map((page, i) => {
      const height = flowedContentHeight(page);
      const fits = height >= BLANK_PAGE_CONTENT_FLOOR;
      return fits && height < contentArea(page) * UNDERFULL_PAGE_FILL_RATIO ? i + 1 : 0;
    })
    .filter((n) => n > 0);
};

const warnOnBlankPages = (layout: LayoutNode | undefined): void => {
  const total = layout?.children?.length ?? 0;
  const blank = findBlankPages(layout);
  if (blank.length > 0) {
    console.error(
      `[PDF-fit] blank page(s) in rendered PDF: ${blank.join(', ')} of ${total}. ` +
        'A near-zero-height node was orphaned by a page break — check for standalone rules/spacers at the end of a section.'
    );
  }

  const underfull = findUnderfullPages(layout);
  if (underfull.length > 0) {
    console.error(
      `[PDF-fit] mostly-empty page(s) in rendered PDF: ${underfull.join(', ')} of ${total}. ` +
        'A section that fit the page was pushed off it — check for `minPresenceAhead` or a trailing margin on a section container.'
    );
  }
};

/**
 * Renders the one-pager, then checks react-pdf's layout tree to see whether the
 * Project Overview card landed entirely on page 1. If it split or was pushed to
 * page 2, re-renders with a smaller `overviewScale` (computed from the measured
 * card height vs. the space actually available on page 1) until it fits or the
 * legibility floor is reached.
 *
 * `makeDoc` must forward `overviewScale` and `onLayout` to <OnePagerPdf>.
 */
export async function renderPdfFittedToPageOne(
  makeDoc: (overviewScale: number, onLayout: (layout: unknown) => void) => ReactElement
): Promise<Blob> {
  let scale = 1;
  let blob: Blob | null = null;
  let lastLayout: LayoutNode | undefined;

  // Every exit from the fit loop reports on the layout that produced the blob it returns.
  const finish = (result: Blob): Blob => {
    warnOnBlankPages(lastLayout);
    return result;
  };

  for (let pass = 0; pass < MAX_FIT_PASSES; pass += 1) {
    const captured: { layout?: LayoutNode } = {};
    const doc = makeDoc(scale, (layout) => {
      captured.layout = layout as LayoutNode;
    });
    console.debug(`[PDF-fit] pass ${pass + 1} rendering at scale ${scale}`);
    blob = await pdf(doc).toBlob();
    console.debug(`[PDF-fit] pass ${pass + 1} rendered, layout captured: ${!!captured.layout}`);
    lastLayout = captured.layout;

    const pages = captured.layout?.children ?? [];
    if (pages.length <= 1) return finish(blob);

    const fragmentsPerPage = pages.map((page) => {
      const found: LayoutNode[] = [];
      collectById(page, OVERVIEW_CARD_ID, found);
      return found;
    });
    const onFirstPage = fragmentsPerPage[0].length > 0;
    const onLaterPages = fragmentsPerPage.slice(1).some((frags) => frags.length > 0);
    const fits = onFirstPage && !onLaterPages;
    // Also bail out if the marker was never found (layout internals changed).
    if (fits || !fragmentsPerPage.some((frags) => frags.length > 0)) return finish(blob);
    if (scale <= MIN_OVERVIEW_SCALE) return finish(blob);

    const firstPage = pages[0];
    const pageHeight = firstPage.style?.height ?? firstPage.box?.height ?? 792;
    // Matches react-pdf's own wrap boundary: page height minus bottom padding.
    const wrapBottom = pageHeight - (firstPage.style?.paddingBottom ?? 0);
    const cardTop = onFirstPage
      ? fragmentsPerPage[0][0].box?.top ?? 0
      : furthestContentBottom(firstPage);
    const available = wrapBottom - cardTop;
    const cardHeight = fragmentsPerPage.reduce(
      (sum, frags) => sum + frags.reduce((s, f) => s + (f.box?.height ?? 0), 0),
      0
    );

    // Height scales roughly linearly with the type scale; 0.97 pads for line-break
    // quantization, and the next pass verifies against the real layout anyway. When the
    // card split, the fragment heights double-count card padding and the page-1 fragment
    // fills the available space exactly, so the ratio estimate approaches 1 — the minimum
    // step below is what actually drives convergence in that case.
    let next =
      cardHeight > 0 && available > 0 ? scale * (available / cardHeight) * 0.97 : scale - 0.1;
    if (!Number.isFinite(next)) next = scale - 0.1;
    next = Math.min(next, scale - 0.04);
    scale = Math.max(MIN_OVERVIEW_SCALE, next);
  }

  return finish(blob as Blob);
}
